/**
 * pd-tabbar.js — the floating mobile bottom navigation.
 *
 * Three jobs:
 *   1. mark the tab matching the current page, so the chip opens on the right one
 *   2. keep the cart badge honest
 *   3. get out of the way while the customer reads, and come back the moment they want it
 *
 * The Reward tab is an anchor to #smile-home. That is Smile.io's documented launcher hook and
 * the same thing the paid app bar used; we let the click through untouched rather than trying
 * to drive Smile's iframe ourselves.
 */
(function () {
  "use strict";

  var HIDE_AFTER = 12;   // px of downward scroll before we get out of the way
  var SHOW_AFTER = 6;    // px of upward scroll to come back — deliberately easier than hiding
  var IDLE_MS = 600;     // stopped scrolling for this long? come back. Long enough that the
                         // pause between two flicks does not count as stopping — at 260ms the
                         // bar flashed back in and out while the customer was still scrolling.

  function currentKey() {
    var path = window.location.pathname.replace(/\/+$/, "") || "/";
    if (path === "/" || /^\/[a-z]{2}(-[a-z]{2})?$/i.test(path)) return "home";
    if (path.indexOf("/cart") === 0) return "cart";
    if (path.indexOf("/account") === 0) return "account";
    if (/\/pages\/wishlist$/.test(path)) return "wishlist";
    return null; // a product or collection page: nothing is "current"
  }

  function markActive(bar) {
    var key = currentKey();
    // A fixed tab (the garage chip) is the bar's permanent highlight; no page lights up another one.
    if (bar.querySelector("[data-tab-fixed]")) {
      bar.querySelectorAll("[data-tab]").forEach(function (a) { var fixed = a.hasAttribute("data-tab-fixed"); a.classList.toggle("is-active", fixed); if (fixed) a.setAttribute("aria-current", "true"); else a.removeAttribute("aria-current"); });
      return;
    }
    bar.querySelectorAll("[data-tab]").forEach(function (a) {
      a.classList.toggle("is-active", !!key && a.dataset.tab === key);
      if (!!key && a.dataset.tab === key) a.setAttribute("aria-current", "page");
      else a.removeAttribute("aria-current");
    });
  }

  /** Cart count, from the cart itself rather than a number rendered at page load. */
  async function syncCart(bar) {
    var el = bar.querySelector("[data-tab-count]");
    if (!el) return;
    try {
      var res = await fetch("/cart.js", { headers: { Accept: "application/json" } });
      if (!res.ok) return;
      var cart = await res.json();
      var n = cart.item_count || 0;
      el.textContent = n > 99 ? "99+" : String(n);
      el.hidden = n === 0;
    } catch (e) {
      /* a missing badge is better than a wrong one */
    }
  }

  /**
   * Hide on the way down, return on the way up or when scrolling stops.
   *
   * Read-only on scroll and all work batched into one rAF: this bar is fixed over every page on
   * the site, and a scroll handler that measures layout is the easiest way to make a phone feel
   * slow.
   */
  function attachScroll(bar) {
    var last = window.scrollY;
    var ticking = false;
    var idle;

    function show() { bar.classList.remove("is-hidden"); }
    function hide() { bar.classList.add("is-hidden"); }

    function onFrame() {
      ticking = false;
      var y = window.scrollY;
      var dy = y - last;

      // Near the top, or past the end of the page, the bar is never in the way.
      if (y < 80 || y + window.innerHeight >= document.documentElement.scrollHeight - 24) {
        show();
      } else if (dy > HIDE_AFTER) {
        hide();
      } else if (dy < -SHOW_AFTER) {
        show();
      }
      if (Math.abs(dy) > 2) last = y;
    }

    window.addEventListener(
      "scroll",
      function () {
        if (!ticking) {
          ticking = true;
          window.requestAnimationFrame(onFrame);
        }
        clearTimeout(idle);
        idle = setTimeout(show, IDLE_MS);
      },
      { passive: true },
    );
  }

  /**
   * pd-nav's exit on a page without pd-nav (the cart): the content and the footer fade out, the page eases to
   * the top, the label-less circle takes the content's place, then the real navigation. It snapped and blinked
   * straight to the next page before (merchant 2026-09-24). Styles: pd-tabbar.css .pd-leaving.
   */
  var leaving = false;
  function leaveHere(href) {
    if (leaving) return;
    leaving = true;
    try { sessionStorage.setItem("pd-arrive", String(Date.now())); } catch (e) {}
    var main = document.getElementById("MainContent");
    var reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (!main || reduce) { window.location.assign(href); return; }
    document.documentElement.classList.add("pd-nav-wait");   // the footer goes with the page (pd-aside.css)
    main.classList.add("pd-leaving");
    window.scrollTo({ top: 0, behavior: "smooth" });
    setTimeout(function () {
      var box = document.createElement("div");
      box.className = "pd-loading pd-leaving__spin";
      box.setAttribute("aria-live", "polite");
      box.innerHTML = '<span class="pd-loading__spin" aria-hidden="true"></span><span class="visually-hidden"></span>';
      var bar = document.querySelector("[data-pd-tabbar]");
      box.lastChild.textContent = (bar && bar.dataset.loadingLabel) || "";   // pd.collection.loading, for screen readers
      main.classList.add("is-left");
      main.appendChild(box);
      setTimeout(function () { window.location.assign(href); }, 650);   // the same minimum as pd-nav's circle
    }, 200);
  }
  // Back to a cart left mid-exit (bfcache): put it back as it was
  window.addEventListener("pageshow", function (e) {
    if (!e.persisted || !leaving) return;
    leaving = false;
    var main = document.getElementById("MainContent");
    document.documentElement.classList.remove("pd-nav-wait");
    if (main) { main.classList.remove("pd-leaving", "is-left"); var b = main.querySelector(".pd-leaving__spin"); if (b) b.remove(); }
  });

  /**
   * The bar waits for the cookie choice. Shopify's privacy banner is injected after load, so: lift the wait
   * when Shopify reports the consent (visitorConsentCollected), when a banner we saw goes away, when the
   * privacy API says no banner is due, or after 3 s without any banner (markets that need none).
   */
  function waitForConsent(bar) {
    if (!bar.classList.contains("is-consent-wait")) return;
    var seen = false, t0 = Date.now(), iv;
    // `decided`: only a real answer is remembered (the next page shows the bar at once). The 3 s fallback is not —
    // on a slow phone the banner came after it and the bar stood over it for good (merchant 2026-09-24). While the
    // banner is up, pd-tabbar.css hides the bar anyway.
    function done(decided) { clearInterval(iv); bar.classList.remove("is-consent-wait"); if (decided) try { localStorage.setItem("pd_consent_done", "1"); } catch (e) {} }
    document.addEventListener("visitorConsentCollected", function () { done(true); });
    iv = setInterval(function () {
      var b = document.getElementById("shopify-pc__banner"), up = false;
      if (b) { var cs = getComputedStyle(b), r = b.getBoundingClientRect(); up = cs.display !== "none" && cs.visibility !== "hidden" && +cs.opacity > 0.05 && r.height > 0; }
      if (up) { seen = true; return; }
      if (seen) return done(true);   // shown, then answered
      if (Date.now() - t0 > 3000) done(false);   // no banner (yet): show the bar, remember nothing
    }, 200);
  }

  function init() {
    var bar = document.querySelector("[data-pd-tabbar]");
    if (!bar || bar.dataset.ready) return;
    bar.dataset.ready = "1";

    markActive(bar);
    syncCart(bar);
    waitForConsent(bar);
    // the cart page: the bar slides away (it covered "Check out"); two frames so it is seen leaving
    if (currentKey() === "cart") requestAnimationFrame(function () { requestAnimationFrame(function () { bar.classList.add("is-away"); }); });
    attachScroll(bar);

    // A tab that opens a panel instead of navigating gets its own beat of feedback, otherwise
    // the tap feels like it did nothing while Smile's iframe wakes up.
    // Every tab that is a page of ours leaves with the site's transition (merchant 2026-09-24: "samme
    // fade out, loading cirkel og selve siden"): pdNav.go swaps the page in place, pdNav.leave is the
    // fade → spinner → load for pages outside our frame (the cart), and the account page — Shopify's
    // own — keeps the plain link. The current page does nothing.
    bar.addEventListener("click", function (ev) {
      var link = ev.target.closest("a[data-tab]");
      if (!link || ev.defaultPrevented || ev.button !== 0 || ev.metaKey || ev.ctrlKey || ev.shiftKey || ev.altKey) return;
      var key = link.dataset.tab, nav = window.pdNav;
      if (key === "account") return;
      var url; try { url = new URL(link.href, location.href); } catch (e) { return; }
      if (url.origin !== location.origin) return;
      if (url.pathname === location.pathname && url.search === location.search) { ev.preventDefault(); return; }
      // The cart page has no pd-nav (no frame, no sidebar): leaveHere() gives it the same fade → circle, and the
      // next page fades in on arrival (snippets/pd-arrive.liquid). Everywhere else pd-nav sets that flag itself.
      if (!nav) { ev.preventDefault(); leaveHere(url.href); return; }
      ev.preventDefault();
      var bare = { bare: true };   // the circle only, no "Henter varer…" (merchant 2026-09-24)
      if (key === "cart") { if (typeof nav.leave === "function") nav.leave(url.href, bare); else location.assign(url.href); return; }
      if (typeof nav.go === "function" && nav.go(url.href, bare)) return;
      if (typeof nav.leave === "function") nav.leave(url.href, bare); else location.assign(url.href);
    });
    bar.addEventListener("click", function (ev) {
      var a = ev.target.closest("[data-tab-panel]");
      if (!a) return;
      // The garage chip opens the app's garage popup through its own (phone-hidden) header button; without
      // the app it goes to the find-parts page. Handled here, not via pd-aside.js, so one tap = one toggle.
      if (a.dataset.tab === "garage") {
        ev.preventDefault();
        var trig = document.querySelector("[data-gw-open]");
        // deferred: the popup's own "click outside closes" listener sits on document and would see THIS
        // tap bubbling up right after the popup opened, and shut it again (pd-aside.js waits the same way)
        if (trig) setTimeout(function () { trig.click(); }, 80); else window.location.href = "/pages/find-dele";
      }
      a.classList.remove("is-pulse");
      void a.offsetWidth; // restart the animation
      a.classList.add("is-pulse");
    });

    // Anything that changes the cart should move the badge.
    ["pd:cart-updated", "cart:updated", "cart:refresh"].forEach(function (e) {
      document.addEventListener(e, function () { syncCart(bar); });
    });
    window.addEventListener("pageshow", function () { syncCart(bar); markActive(bar); });
  }

  if (document.readyState === "loading") document.addEventListener("DOMContentLoaded", init);
  else init();
})();
